import { ArrowDownTrayIcon, CheckCircleIcon, DocumentArrowUpIcon, DocumentTextIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useState } from 'react';

import { formatDate } from '../../../../common-utils/formatters';
import type { Company } from '../../../@types/Company';
import { SOFTWARE_CONFIGS } from '../../../@types/Company';
import { IMPORT_SOURCES } from '../../../@types/ImportSource';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';

interface SoftwareExportUploadProps {
  company: Company;
  jwt: string | null;
  onUploadSuccess: (updatedCompany: Company) => void;
  onExport: () => Promise<void> | void;
  exporting: boolean;
}

const SoftwareExportUpload: React.FC<SoftwareExportUploadProps> = ({
  company,
  jwt,
  onUploadSuccess,
  onExport,
  exporting,
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const softwareConfig = SOFTWARE_CONFIGS.find((c) => c.id === company.selectedSoftware);
  const hasFile = Boolean(company.configExportFileName);

  const handleFile = useCallback(async(file: File) => {
    if (!jwt || !company.id) {
      return;
    }

    const validExtensions = ['.xls', '.xlsx', '.csv'];
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      showToast('error', 'Format de fichier non supporté. Veuillez utiliser un fichier Excel (.xls, .xlsx) ou CSV.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`/api/companies/${company.id}/config-export`, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const updatedCompany = await response.json();
      onUploadSuccess(updatedCompany);
      showToast('success', 'Fichier envoyé avec succès.');
    } catch (error) {
      console.error('Upload error :', error);
      showToast('error', 'Une erreur est survenue lors de l\'envoi du fichier.');
    } finally {
      setUploading(false);
    }
  }, [jwt, company.id, onUploadSuccess]);

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleDownloadPdf = () => {
    if (softwareConfig?.pdfUrl) {
      window.open(softwareConfig.pdfUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
      <h2 className="mb-4 flex flex-col items-start gap-3 text-xl font-bold text-gray-800 md:flex-row md:items-center">
        <span className="rounded bg-emerald-600 px-3 py-1 text-sm tracking-wide whitespace-nowrap text-white uppercase">ÉTAPE SUIVANTE :</span>
        <span>TÉLÉCHARGER LE TUTORIEL ET ENVOYER VOTRE FICHIER</span>
      </h2>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* PDF Download Section */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-3 font-semibold text-gray-800">1. Tutoriel d'export</h3>
          {company.selectedSoftware === IMPORT_SOURCES.RNIC_EXCEL ? (
            <div className="space-y-4 text-sm text-gray-700">
              <p>
                Ce fichier est généré directement à partir de vos données du registre national des copropriétés (RNIC). Suivez les étapes ci-dessous pour le compléter, puis renvoyez-le dans le bloc à droite.
              </p>

              <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 md:flex-row md:items-center">
                <span className="text-sm font-semibold tracking-wide text-gray-700 uppercase">Documents d'aide :</span>
                <div className="flex flex-1 flex-col gap-2 md:flex-row md:flex-wrap">
                  <a
                    href="/downloads/tutoriel-1-prealable-validation-immeubles-geres.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100"
                  >
                    <DocumentTextIcon className="h-5 w-5" />
                    Tutoriel préalable (PDF)
                  </a>
                  <a
                    href="/downloads/fichier-rnic-anonymise.xlsx"
                    download
                    className="inline-flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-700 transition-colors hover:bg-green-100"
                  >
                    <ArrowDownTrayIcon className="h-5 w-5" />
                    Exemple de fichier RNIC anonymisé (Excel)
                  </a>
                </div>
              </div>

              <div>
                <p className="mb-2 font-semibold text-gray-800">Étape 1 — Exporter le fichier</p>
                <button
                  onClick={() => { void onExport(); }}
                  disabled={exporting}
                  className="hover:bg-opacity-90 flex items-center gap-2 rounded-lg bg-rgpd-blue-light px-4 py-2 font-semibold text-white transition-all disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {exporting ? (
                    <Spinner size="sm" />
                  ) : (
                    <ArrowDownTrayIcon className="h-5 w-5" />
                  )}
                  Télécharger le fichier Excel à traiter
                </button>
              </div>

              <div>
                <p className="mb-2 font-semibold text-gray-800">Étape 2 — Compléter le fichier</p>
                <ul className="list-disc space-y-2 pl-5">
                  <li>
                    Remplir les <strong>cellules surlignées en jaune</strong> (informations manquantes que vous connaissez : date de fin de mandat, date AG, attributions gestionnaire / transactionnaire / gestionnaire locatif, etc.).
                  </li>
                  <li>
                    Ajuster la première colonne <strong>« Copro validée »</strong> : <code>Oui</code> pour les copropriétés que vous gérez encore, <code>Non</code> pour celles à exclure de votre parc.
                  </li>
                  <li>
                    Ajouter une ligne pour chacun de vos <strong>ASL, AFUL et immeubles tertiaires</strong> qui n'apparaissent pas dans le registre. Renseigner la colonne <strong>« Type d'immeuble »</strong> avec l'une des valeurs : <code>Copropriété</code>, <code>ASL</code>, <code>AFUL</code>, <code>Tertiaire</code> ou <code>Parking</code> (mêmes valeurs que l'ajout manuel à l'étape suivante). Laisser la colonne « Identifiant RNIC » vide pour ces lignes.
                  </li>
                </ul>
              </div>

              <div>
                <p className="mb-2 font-semibold text-gray-800">Étape 3 — Envoyer le fichier complété</p>
                <p>
                  Déposer le fichier modifié dans le bloc « Envoi du fichier exporté » à droite.
                </p>
              </div>
            </div>
          ) : softwareConfig?.hasPdf ? (
            <div>
              <p className="mb-3 text-sm text-gray-600">
                Téléchargez le tutoriel pour savoir comment exporter vos données depuis {softwareConfig.displayName}.
              </p>
              <button
                onClick={handleDownloadPdf}
                className="hover:bg-opacity-90 flex items-center gap-2 rounded-lg bg-rgpd-blue-light px-4 py-2 font-semibold text-white transition-all"
              >
                <ArrowDownTrayIcon className="h-5 w-5" />
                Télécharger le tutoriel PDF
              </button>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm text-amber-800">
                Le tutoriel pour <strong>{softwareConfig?.displayName}</strong> est en cours de rédaction.
              </p>
              <p className="mt-2 text-sm text-amber-700">
                Merci de nous contacter par email pour plus d'informations et d'aide à la mise en place et l'export.
              </p>
            </div>
          )}
        </div>

        {/* File Upload Section */}
        <div className="flex flex-col rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="mb-3 font-semibold text-gray-800">2. Envoi du fichier exporté</h3>

          {hasFile ? (
            <div className="flex flex-1 flex-col rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <CheckCircleIcon className="h-6 w-6 text-emerald-600" />
                <span className="font-semibold text-emerald-800">Fichier envoyé</span>
              </div>
              <p className="mb-3 text-sm text-emerald-700">
                Fichier envoyé le {company.configExportUploadedAt ? formatDate(company.configExportUploadedAt) : '-'}
              </p>
              <p className="mb-3 text-sm text-gray-600">
                Vous pouvez envoyer un nouveau fichier pour le remplacer.
              </p>
              <div
                className={`flex flex-1 items-center justify-center rounded-lg border-2 border-dashed p-4 text-center transition-colors ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <label htmlFor="file-upload-replace" className="flex w-full cursor-pointer items-center justify-center">
                  <span className="text-sm text-gray-500">
                    {uploading ? (
                      <Spinner size="sm" />
                    ) : (
                      <>Glissez un fichier ou cliquez pour remplacer</>
                    )}
                  </span>
                  <input
                    id="file-upload-replace"
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".xls,.xlsx,.csv"
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>
          ) : (
            <div
              className={`flex flex-1 cursor-pointer rounded-lg border-2 border-dashed transition-colors ${dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-white'}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <label htmlFor="file-upload" className="flex min-h-[12rem] w-full cursor-pointer flex-col items-center justify-center py-8">
                {uploading ? (
                  <Spinner size="lg" />
                ) : (
                  <>
                    <DocumentArrowUpIcon className="mb-3 h-10 w-10 text-gray-400" />
                    <p className="mb-2 text-sm text-gray-600">
                      <span className="font-semibold">Cliquez pour télécharger</span> ou glissez et déposez
                    </p>
                    <p className="text-xs text-gray-500">XLS, XLSX, CSV (MAX. 10Mo)</p>
                  </>
                )}
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  accept=".xls,.xlsx,.csv"
                  disabled={uploading}
                />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SoftwareExportUpload;
